/**
 * Juni-2026 (User-Wunsch) — CSV-Import „im Vorlauf".
 *
 * Importiert bestellte (noch nicht eingetroffene) Fahrzeuge als Entwurf
 * (Vorlauf, nicht öffentlich). Ablauf: Vorlage laden → CSV wählen → Vorschau
 * mit Validierung → Import. Gleiche 10 Felder wie die Gruppenbearbeitung.
 */
import { useState, useRef } from "react";
import { X, Loader2, Download, Upload, FileSpreadsheet, AlertTriangle, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";

export default function DispoCsvImportModal({ onClose, onDone }) {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [fileName, setFileName] = useState("");
    const fileRef = useRef(null);

    const downloadTemplate = async () => {
        try {
            const r = await api.get("/dealer/dispo/csv-template", { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = "Vorlauf-Import-Vorlage.csv";
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Vorlage konnte nicht geladen werden.");
        }
    };

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setFileName(file.name);
        setLoading(true);
        setPreview(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/dealer/dispo/csv-import/preview", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPreview(r.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "CSV konnte nicht gelesen werden.");
        } finally {
            setLoading(false);
        }
    };

    const commit = async () => {
        if (!preview) return;
        const validRows = (preview.rows || []).filter((r) => (r.errors || []).length === 0);
        if (validRows.length === 0) {
            toast.error("Keine gültigen Zeilen zum Import.");
            return;
        }
        setImporting(true);
        try {
            const r = await api.post("/dealer/dispo/csv-import/commit", { rows: validRows });
            toast.success(`${r.data?.created_count ?? 0} Fahrzeug(e) im Vorlauf angelegt.`);
            onDone?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Import fehlgeschlagen.");
        } finally {
            setImporting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto"
            data-testid="dispo-csv-import-modal"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
            <div className="w-full max-w-3xl mt-10 bg-white border border-gray-200 rounded-sm shadow-xl">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-[#111827] text-white rounded-t-sm">
                    <h2 className="font-display text-lg font-black flex items-center gap-2">
                        <FileSpreadsheet className="w-5 h-5 text-[#FFCC00]" /> CSV-Import (Vorlauf)
                    </h2>
                    <button type="button" onClick={onClose} className="p-1 rounded-sm hover:bg-white/10" aria-label="Schließen" data-testid="dispo-csv-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-5 space-y-4 max-h-[68vh] overflow-y-auto">
                    <p className="text-xs text-gray-500">
                        Importiert bestellte Fahrzeuge „im Vorlauf" als <strong>Entwurf</strong> (noch nicht öffentlich sichtbar, aber in der Disposition). Spalten: Titel, Hersteller-Modell, <strong>Fahrgestellnummer (FIN)</strong>, Verkaufspreis, Hersteller-Farbe, Farbbasis, Hersteller-Bestellcode, Standort, Standplatz, Interne Vermerke, Status. Mit FIN erkennt der spätere Sammel-Scan das Fahrzeug automatisch.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                        <Button type="button" variant="outline" onClick={downloadTemplate} className="h-9 rounded-sm text-sm" data-testid="dispo-csv-template-btn">
                            <Download className="w-4 h-4 mr-1.5" /> Vorlage herunterladen
                        </Button>
                        <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="dispo-csv-file-input" />
                        <Button type="button" onClick={() => fileRef.current?.click()} className="h-9 rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black text-sm font-bold" data-testid="dispo-csv-choose-btn">
                            <Upload className="w-4 h-4 mr-1.5" /> CSV wählen
                        </Button>
                        {fileName && <span className="text-xs text-gray-600 truncate">{fileName}</span>}
                        {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-500" />}
                    </div>

                    {preview && (
                        <div data-testid="dispo-csv-preview">
                            <div className="flex items-center gap-3 text-xs font-bold mb-2">
                                <span className="text-emerald-700 inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> {preview.valid} gültig</span>
                                {preview.invalid > 0 && <span className="text-red-700 inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {preview.invalid} fehlerhaft</span>}
                                <span className="text-gray-500">{preview.total} Zeilen gesamt</span>
                            </div>
                            <div className="border border-gray-200 rounded-sm overflow-auto max-h-72">
                                <table className="w-full text-[11px]">
                                    <thead className="bg-gray-100 text-gray-600 sticky top-0">
                                        <tr>
                                            <th className="text-left px-2 py-1.5">Titel</th>
                                            <th className="text-left px-2 py-1.5">Modell</th>
                                            <th className="text-left px-2 py-1.5">FIN</th>
                                            <th className="text-right px-2 py-1.5">Preis</th>
                                            <th className="text-left px-2 py-1.5">Farbe</th>
                                            <th className="text-left px-2 py-1.5">Standort</th>
                                            <th className="text-left px-2 py-1.5">Status</th>
                                            <th className="text-left px-2 py-1.5">Prüfung</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(preview.rows || []).map((r, i) => {
                                            const row = r.row || {};
                                            const ok = (r.errors || []).length === 0;
                                            return (
                                                <tr key={i} className={ok ? "" : "bg-red-50"} data-testid={`dispo-csv-row-${i}`}>
                                                    <td className="px-2 py-1 truncate max-w-[160px]">{row.title || "—"}</td>
                                                    <td className="px-2 py-1">{row.model || "—"}</td>
                                                    <td className="px-2 py-1 font-mono-num">{row.vin || "—"}</td>
                                                    <td className="px-2 py-1 text-right font-mono-num">{row.price != null ? row.price : "—"}</td>
                                                    <td className="px-2 py-1">{row.color_base || row.color || "—"}</td>
                                                    <td className="px-2 py-1">{row.site_code || "—"}</td>
                                                    <td className="px-2 py-1">{row.status_label || "—"}</td>
                                                    <td className="px-2 py-1">
                                                        {ok ? <span className="text-emerald-600 font-bold">OK</span>
                                                            : <span className="text-red-600">{(r.errors || []).join("; ")}</span>}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-sm">
                    <Button type="button" variant="outline" onClick={onClose} className="h-9 rounded-sm text-sm" data-testid="dispo-csv-cancel">Abbrechen</Button>
                    <Button type="button" onClick={commit} disabled={importing || !preview || (preview?.valid ?? 0) === 0}
                        className="h-9 rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black text-sm font-bold" data-testid="dispo-csv-commit">
                        {importing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                        {preview?.valid ? `${preview.valid} importieren` : "Importieren"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
